// Thin hook-based API wrapper for /fleet/api/* endpoints.
//
// apiFetch lives inside the useApi hook (src/api.js / src/api.ts) and is not
// a free-standing import — it is obtained from context and threaded down to
// callers. FleetAPI bundles the typed fetch helpers so each page shell only
// needs to call useFleetApi() and destructure what it needs.

import { useCallback } from "react";
import { useApi } from "../api";

export type AuditRow = {
  id: number;
  trace_id: string;
  agent: string;
  kind: string;
  tool: string | null;
  latency_ms: number | null;
  created_at: string;
};

export type ActionRow = {
  id: number;
  trace_id: string;
  agent: string;
  kind: string;
  subject: string | null;
  reversible: boolean;
  reverted_at: string | null;
  created_at: string;
};

export type ActionStep = { action: string } & Record<string, unknown>;

export type EscalationOption = {
  label: string;
  recommended?: boolean;
  /** When set to "reject", resolve closes the card without dispatching actions. */
  verdict?: "reject" | string;
  // New compound contract: ordered list of actions dispatched atomically.
  actions?: ActionStep[];
  // Legacy single-action contract.
  payload_override?: Record<string, unknown>;
};

export type EscalationRow = {
  id: number;
  trace_id: string;
  agent: string;
  situation_key: string;
  situation_class: string;
  decision_class: string;
  reasoning: string;
  proposed_payload: Record<string, unknown>;
  options: EscalationOption[];
  created_at: string;
  is_test?: boolean;
};

export type KillSwitch = {
  endpoint: string;
  enabled: boolean;
  reason: string | null;
  updated_at: string;
  updated_by: string | null;
};

export type BoundsRow = {
  agent: string;
  situation_class: string;
  decision_class: string;
  mode: string | null;
  since: string | null;
  reason: string | null;
};

export type OverviewSummary = {
  escalations: { open: number; resolved: number; overridden: number };
  actions: { last_24h: number; total: number; reverted: number };
  audit: { last_24h: number; total: number; distinct_agents: number; last_event_at: string | null };
  tasks: { by_status: Record<string, number>; failed_last_24h: number };
  top_agents_7d: Array<{ agent: string; events_7d: number }>;
};

export type TaskRow = {
  id: number;
  caller: string;
  callee: string;
  trigger: string;
  status: "pending" | "running" | "resolved" | "failed" | string;
  created_at: string;
  started_at: string | null;
  resolved_at: string | null;
  expires_at: string | null;
  failure_reason: string | null;
  trigger_payload: Record<string, unknown>;
};

export type AutonomyMode =
  | "escalate_every_write"
  | "auto_under_threshold"
  | "auto"
  | "disabled";

export type AutonomySignalRow = {
  situation_class: string;
  decision_class: string;
  mode: AutonomyMode | null;
  approvals_count: number;
  approvals_last_30d?: number;
  last_approval_at: string | null;
  last_revert_at: string | null;
  // populated by setAutonomy responses only — the GET list endpoint omits these.
  reason?: string | null;
  since?: string | null;
};

export type SetAutonomyBody = {
  situation_class: string;
  decision_class: string;
  mode: AutonomyMode;
  reason?: string | null;
  actor: string;
};

export type AgentRow = {
  agent: string;
  last_seen: string | null;
  audit_total: number;
  audit_24h: number;
  open_escalations: number;
  tasks_failed_24h: number;
  tasks_resolved_24h: number;
  enabled: boolean | null;
  last_success_at?: string | null;
  last_error_at?: string | null;
  consecutive_errors: number | null;
  autonomy_signal?: AutonomySignalRow[];
};

export type RuntimeStatus = {
  tasks_by_status: Record<string, number>;
  pending: { count: number; oldest_at: string | null };
  last_hour: { resolved: number; failed: number };
  recent_failures: Array<{
    id: number;
    callee: string;
    failure_reason: string | null;
    created_at: string;
  }>;
};

export type KillSwitchFlipBody = {
  endpoint: string;
  enabled: boolean;
  reason: string | null;
  actor: string;
};

export type TraceTimelineRow = {
  row_type: "audit" | "action" | "escalation";
  [key: string]: unknown;
};

export type UnifiedTrace = {
  trace_id: string;
  counts: { audit: number; action: number; escalation: number };
  timeline: TraceTimelineRow[];
};

export type ActivityRow = {
  row_type: "audit" | "action" | "escalation";
  id: number;
  trace_id: string;
  agent: string;
  created_at: string;
  // audit fields
  parent_trace_id?: string | null;
  kind?: string | null;
  tool?: string | null;
  latency_ms?: number | null;
  // action fields
  subject?: string | null;
  reversible?: boolean | null;
  reverted_at?: string | null;
  // escalation fields
  situation_key?: string | null;
  situation_class?: string | null;
  decision_class?: string | null;
  status?: string | null;
  resolution?: string | null;
  resolved_at?: string | null;
};

export type ActivityFeed = {
  counts: { audit: number; action: number; escalation: number };
  timeline: ActivityRow[];
};

export type ResolveBody = {
  verdict: string;
  actor: string;
  refine_payload?: Record<string, unknown>;
  refine_text?: string;
  option_index?: number;
};

export type BulkRejectResult = {
  ok: boolean;
  rejected: number[];
  skipped_open: number[];
  errors: Array<{ escalation_id: number; code?: string; message: string }>;
  except_ids: number[];
};

export function useFleetApi(token: string | null, logout: () => void) {
  const { apiFetch } = useApi(token, logout);

  const fetchAuditRecent = useCallback(
    async (opts?: { agent?: string; limit?: number }): Promise<AuditRow[]> => {
      const q = new URLSearchParams();
      if (opts?.agent) q.set("agent", opts.agent);
      if (opts?.limit) q.set("limit", String(opts.limit));
      const res = await apiFetch(`/fleet/api/audit/recent?${q}`);
      return res.json();
    },
    [apiFetch],
  );

  const fetchActionRecent = useCallback(
    async (opts?: { agent?: string; kind?: string; limit?: number }): Promise<ActionRow[]> => {
      const q = new URLSearchParams();
      if (opts?.agent) q.set("agent", opts.agent);
      if (opts?.kind) q.set("kind", opts.kind);
      if (opts?.limit) q.set("limit", String(opts.limit));
      const res = await apiFetch(`/fleet/api/action/recent?${q}`);
      return res.json();
    },
    [apiFetch],
  );

  const fetchEscalationsOpen = useCallback(
    async (opts?: { limit?: number; includeTest?: boolean }): Promise<EscalationRow[]> => {
      const q = new URLSearchParams();
      if (opts?.limit) q.set("limit", String(opts.limit));
      if (opts?.includeTest) q.set("include_test", "true");
      const res = await apiFetch(`/fleet/api/escalation/open?${q}`);
      return res.json();
    },
    [apiFetch],
  );

  const fetchKillSwitches = useCallback(async (): Promise<KillSwitch[]> => {
    const res = await apiFetch(`/fleet/api/kill-switches`);
    return res.json();
  }, [apiFetch]);

  const resolveEscalation = useCallback(
    async (id: number, body: ResolveBody): Promise<Record<string, unknown>> => {
      const res = await apiFetch(`/fleet/api/escalation/${id}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`resolve failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const bulkRejectEscalations = useCallback(
    async (exceptIds: number[]): Promise<BulkRejectResult> => {
      const res = await apiFetch(`/fleet/api/escalation/bulk-reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ except_ids: exceptIds }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`bulk reject failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const fetchTrace = useCallback(
    async (traceId: string, limit = 500): Promise<Record<string, unknown>[]> => {
      const q = new URLSearchParams({ limit: String(limit) });
      const res = await apiFetch(`/fleet/api/audit/${encodeURIComponent(traceId)}/full?${q}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`fetchTrace failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const fetchBounds = useCallback(async (): Promise<BoundsRow[]> => {
    const res = await apiFetch(`/fleet/api/bounds`);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`fetchBounds failed: ${res.status} ${text}`);
    }
    return res.json();
  }, [apiFetch]);

  const fetchOverview = useCallback(async (): Promise<OverviewSummary> => {
    const res = await apiFetch(`/fleet/api/overview`);
    return res.json();
  }, [apiFetch]);

  const fetchTasksRecent = useCallback(
    async (opts?: { status?: string; limit?: number }): Promise<TaskRow[]> => {
      const q = new URLSearchParams();
      if (opts?.status) q.set("status", opts.status);
      if (opts?.limit) q.set("limit", String(opts.limit));
      const res = await apiFetch(`/fleet/api/tasks/recent?${q}`);
      return res.json();
    },
    [apiFetch],
  );

  const fetchAgents = useCallback(async (): Promise<AgentRow[]> => {
    const res = await apiFetch(`/fleet/api/agents`);
    return res.json();
  }, [apiFetch]);

  const fetchRuntime = useCallback(async (): Promise<RuntimeStatus> => {
    const res = await apiFetch(`/fleet/api/runtime`);
    return res.json();
  }, [apiFetch]);

  const fetchUnifiedTrace = useCallback(
    async (traceId: string): Promise<UnifiedTrace> => {
      const res = await apiFetch(`/fleet/api/trace/${encodeURIComponent(traceId)}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`fetchUnifiedTrace failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const fetchActivity = useCallback(
    async (opts?: { agent?: string; limit?: number }): Promise<ActivityFeed> => {
      const q = new URLSearchParams();
      if (opts?.agent) q.set("agent", opts.agent);
      if (opts?.limit) q.set("limit", String(opts.limit));
      const res = await apiFetch(`/fleet/api/activity?${q}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`fetchActivity failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const flipKillSwitch = useCallback(
    async (body: KillSwitchFlipBody): Promise<KillSwitch> => {
      const res = await apiFetch(`/fleet/api/kill-switches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`flipKillSwitch failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  const setAutonomy = useCallback(
    async (agentId: string, body: SetAutonomyBody): Promise<AutonomySignalRow> => {
      const res = await apiFetch(
        `/fleet/api/agents/${encodeURIComponent(agentId)}/autonomy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`setAutonomy failed: ${res.status} ${text}`);
      }
      return res.json();
    },
    [apiFetch],
  );

  return {
    fetchAuditRecent,
    fetchActionRecent,
    fetchEscalationsOpen,
    fetchKillSwitches,
    resolveEscalation,
    bulkRejectEscalations,
    fetchTrace,
    fetchBounds,
    fetchOverview,
    fetchTasksRecent,
    fetchAgents,
    fetchRuntime,
    fetchUnifiedTrace,
    fetchActivity,
    flipKillSwitch,
    setAutonomy,
  };
}
